import React, { useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import { useSelector } from 'react-redux'
import {
  createConquestAd,
  updateConquestAd,
} from 'actions/views/shops'
import useAction from 'utils/useAction'
import { Spin, Button, Typography, Divider, Row, Col, Upload, Image, Modal, message } from 'antd'
import { UploadOutlined, DeleteOutlined } from '@ant-design/icons'
import { Field, Form } from 'react-final-form'
import Input from 'components/Input/Input'
import TextArea from 'components/TextArea/TextArea'
import InputNumber from 'components/Input/InputNumber'
import Select from 'components/Select/Select'
import DatePicker from 'components/DatePicker/DatePicker'
import SearchRules from 'components/SearchRules'
import LinkConverter from 'pages/ArcAd/AdForm/LinkConverter'
import { FIELD_NAMES } from 'const/forms'
import { required } from 'helpers/validators'
import { getIsSavingActive, getConquestAdById } from 'domains/shops/selectors'
import { PARTNER_OFFER_STATUS_OPTIONS } from 'const/shops'
import { EXCLUSIVE_STATUS } from 'const/coupons'
import {
  StyledForm,
  FormSection,
  FullWidthFieldWrapper,
  ButtonContainer,
  OfferUrlWrapper,
  ImageUploadSection,
  ImagePreview,
} from './ConquestAd.styles'

const { Title, Text } = Typography

const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif']
const MAX_IMAGE_SIZE = 2 * 1024 * 1024 // 2MB

const ConquestAd = ({ id, closeModal }) => {
  const isSavingActive = useSelector(getIsSavingActive)
  const existingAd = useSelector(getConquestAdById)(id)
  const initialValues = id ? existingAd?.attributes || {} : {}
  const createConquestAdFunc = useAction(createConquestAd)
  const updateConquestAdFunc = useAction(updateConquestAd)

  const [imageFile, setImageFile] = useState(null)
  const [imagePreview, setImagePreview] = useState(initialValues?.image_url || null)
  const [isUploading, setIsUploading] = useState(false)
  const [removedImage, setRemovedImage] = useState(false)

  const onSubmit = (data) => {
    const values = {
      ...data,
      [FIELD_NAMES.id]: id,
    }

    if (imageFile && imagePreview && imagePreview.startsWith('data:')) {
      values[FIELD_NAMES.imageUrlBase64] = imagePreview
    }

    if (removedImage && !imageFile) {
      values[FIELD_NAMES.clearAdImage] = true
    }

    if (id) {
      updateConquestAdFunc({ values, callback: closeModal })
    } else {
      createConquestAdFunc({ values, callback: closeModal })
    }
  }

  const convertFileToBase64 = useCallback((file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.readAsDataURL(file)
      reader.onload = () => resolve(reader.result)
      reader.onerror = error => reject(error)
    })
  }, [])

  const handleImageUpload = useCallback(async (file) => {
    try {
      if (file.size > MAX_IMAGE_SIZE) {
        message.error('File size must be less than 2MB')
        return false
      }
      if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
        message.error('Please upload a valid image file (JPEG, PNG, GIF)')
        return false
      }
      setIsUploading(true)
      const base64 = await convertFileToBase64(file)
      setImageFile(file)
      setImagePreview(base64)
      setRemovedImage(false)
      message.success('Image uploaded successfully')
      return false
    } catch (error) {
      message.error('Failed to upload image. Please try again.')
      return false
    } finally {
      setIsUploading(false)
    }
  }, [convertFileToBase64])

  const handleRemoveImage = useCallback(() => {
    Modal.confirm({
      title: 'Remove Image',
      content: 'Are you sure you want to remove the current image? The image will be removed when you save the form.',
      okText: 'Yes, Remove',
      cancelText: 'Cancel',
      okType: 'danger',
      onOk() {
        setImageFile(null)
        setImagePreview(null)
        setRemovedImage(true)
        message.info('Image marked for removal. Save the form to apply changes.')
      },
    })
  }, [])

  const handleCopyUrl = useCallback((url) => {
    navigator.clipboard.writeText(url)
    message.success('URL copied to clipboard')
  }, [])

  // Rule is only consumed by the API together with a coupon code (on create),
  // and must belong to the shop — so require it only when a coupon is entered.
  const validateRuleWithCoupon = (value, allValues) =>
    allValues?.[FIELD_NAMES.couponCode] && !value ? 'Required' : undefined

  const getInitialValues = () => {
    if (!id) {
      return {
        [FIELD_NAMES.status]: 'not_opened',
        [FIELD_NAMES.exclusive]: false,
      }
    }

    return {
      ...initialValues,
      [FIELD_NAMES.id]: id,
    }
  }

  return (
    <Form
      destroyOnUnregister
      onSubmit={onSubmit}
      initialValues={getInitialValues()}
      render={({ values, handleSubmit, pristine, form }) => (
        <Spin spinning={isSavingActive} size="large">
          <StyledForm onSubmit={handleSubmit}>
            <FormSection>
              <Title level={5}>Basic Information</Title>
              <Row gutter={[16, 0]}>
                {id && (
                  <Col span={24}>
                    <FullWidthFieldWrapper>
                      <Field
                        name={FIELD_NAMES.id}
                        label="ID"
                        disabled
                        component={Input}
                      />
                    </FullWidthFieldWrapper>
                  </Col>
                )}
                <Col span={24}>
                  <FullWidthFieldWrapper>
                    <Field
                      name={FIELD_NAMES.title}
                      label="Title"
                      placeholder="Title"
                      component={Input}
                    />
                  </FullWidthFieldWrapper>
                </Col>
                <Col span={24}>
                  <FullWidthFieldWrapper>
                    <Field
                      name={FIELD_NAMES.description}
                      label="Description"
                      placeholder="Description"
                      component={TextArea}
                    />
                  </FullWidthFieldWrapper>
                </Col>
                <Col span={24}>
                  <FullWidthFieldWrapper>
                    <Field
                      name={FIELD_NAMES.allowedDomains}
                      label="Allowed Domains"
                      placeholder="Type a competitor domain and press Enter"
                      component={Select}
                      mode="tags"
                      tokenSeparators={[',']}
                      options={[]}
                    />
                  </FullWidthFieldWrapper>
                </Col>
              </Row>
            </FormSection>

            <Divider />

            <FormSection>
              <Title level={5}>Offer URL</Title>
              <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
                Use the Link Converter to generate a tracking URL from a destination link
              </Text>
              <LinkConverter
                onUrlConverted={(url) => form.change(FIELD_NAMES.offerUrl, url)}
              />
              <FullWidthFieldWrapper>
                <Field
                  required
                  name={FIELD_NAMES.offerUrl}
                  validate={required}
                  label="Offer URL"
                  placeholder="Use Link Converter above to generate"
                  component={Input}
                />
              </FullWidthFieldWrapper>
              <OfferUrlWrapper>
                {values[FIELD_NAMES.offerUrl] && (
                  <Button
                    size="small"
                    onClick={() => handleCopyUrl(values[FIELD_NAMES.offerUrl])}
                  >
                    Copy URL
                  </Button>
                )}
              </OfferUrlWrapper>
            </FormSection>

            <Divider />

            <FormSection>
              <Title level={5}>Image</Title>
              <ImageUploadSection>
                {imagePreview ? (
                  <ImagePreview>
                    <Image
                      width={200}
                      src={imagePreview}
                      alt="Conquest ad image"
                      style={{ borderRadius: 8 }}
                    />
                    <Button
                      type="primary"
                      danger
                      size="small"
                      icon={<DeleteOutlined />}
                      onClick={handleRemoveImage}
                      style={{ marginTop: 8 }}
                      disabled={isUploading}
                    >
                      Remove
                    </Button>
                  </ImagePreview>
                ) : (
                  <Upload
                    beforeUpload={handleImageUpload}
                    accept="image/*"
                    showUploadList={false}
                    disabled={isUploading}
                  >
                    <Button
                      icon={<UploadOutlined />}
                      loading={isUploading}
                      disabled={isUploading}
                    >
                      {isUploading ? 'Uploading...' : 'Upload Image'}
                    </Button>
                  </Upload>
                )}
              </ImageUploadSection>
            </FormSection>

            <Divider />

            <FormSection>
              <Title level={5}>Settings</Title>
              <Row gutter={[16, 0]}>
                <Col span={12}>
                  <FullWidthFieldWrapper>
                    <Field
                      required
                      name={FIELD_NAMES.status}
                      validate={required}
                      label="Status"
                      options={PARTNER_OFFER_STATUS_OPTIONS}
                      component={Select}
                    />
                  </FullWidthFieldWrapper>
                </Col>
                <Col span={12}>
                  <FullWidthFieldWrapper>
                    <Field
                      name={FIELD_NAMES.exclusive}
                      label="Exclusive"
                      options={EXCLUSIVE_STATUS}
                      component={Select}
                    />
                  </FullWidthFieldWrapper>
                </Col>
                <Col span={12}>
                  <FullWidthFieldWrapper>
                    <Field
                      name={FIELD_NAMES.priority}
                      label="Priority"
                      min={0}
                      component={InputNumber}
                    />
                  </FullWidthFieldWrapper>
                </Col>
              </Row>
            </FormSection>

            <Divider />

            <FormSection>
              <Title level={5}>Schedule</Title>
              <Row gutter={[16, 0]}>
                <Col span={12}>
                  <FullWidthFieldWrapper>
                    <Field
                      required
                      name={FIELD_NAMES.startedAt}
                      validate={required}
                      label="Started At"
                      component={DatePicker}
                      showTime
                    />
                  </FullWidthFieldWrapper>
                </Col>
                <Col span={12}>
                  <FullWidthFieldWrapper>
                    <Field
                      required
                      name={FIELD_NAMES.expiredAt}
                      validate={required}
                      label="Expired At"
                      component={DatePicker}
                      showTime
                    />
                  </FullWidthFieldWrapper>
                </Col>
              </Row>
            </FormSection>

            {!id && (
              <>
                <Divider />
                <FormSection>
                  <Title level={5}>Coupon</Title>
                  <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
                    Optionally add a coupon code to auto-create a linked Shop Offer. A rule
                    (belonging to this shop) is required when a coupon code is provided.
                  </Text>
                  <Row gutter={[16, 0]}>
                    <Col span={12}>
                      <FullWidthFieldWrapper>
                        <Field
                          name={FIELD_NAMES.couponCode}
                          label="Coupon Code"
                          placeholder="Optional coupon code"
                          component={Input}
                        />
                      </FullWidthFieldWrapper>
                    </Col>
                    <Col span={12}>
                      <FullWidthFieldWrapper>
                        <SearchRules
                          validate={validateRuleWithCoupon}
                          disabled={false}
                          name={FIELD_NAMES.ruleId}
                          mode={null}
                          label="Rule"
                        />
                      </FullWidthFieldWrapper>
                    </Col>
                  </Row>
                </FormSection>
              </>
            )}

            <ButtonContainer>
              <Button onClick={closeModal}>Cancel</Button>
              <Button
                type="primary"
                disabled={pristine}
                onClick={handleSubmit}
              >
                Save changes
              </Button>
            </ButtonContainer>
          </StyledForm>
        </Spin>
      )}
    />
  )
}

ConquestAd.propTypes = {
  id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  closeModal: PropTypes.func.isRequired,
}

ConquestAd.defaultProps = {
  id: null,
}

export default ConquestAd
